import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetStatusEnum, getAttendanceSheetStatusEnumOptions } from '../../../enums/attendance-sheet-status.enum';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { GetPredios } from '@/modules/structure/predio/predio.service';
import { IOpcao } from '@/shared/types/selectOption';
import { InternBondEnum } from '@/modules/business/attendance-sheet/enums/intern-bond.enum';
import { ListAllIntershipsOfTheMonthResponseDto, AttendanceSheetResponseDto } from '@/api.types';
import { PermissionEnum } from '@/modules/system/papeis/enum/permission.enum';
import { RoleEnum } from '@/modules/system/papeis/enum/role.enum';
import { listAllIntershipsOfTheMonth, generateAttendanceSheetsForMonth, createAttendanceSheet } from '../../../attendance-sheet.service';
import { readEstagiarioForAttendanceSheetSelect } from '@/modules/people/estagiario.service';
import { readPersonResponsibleSelect } from '@/modules/people/person.service';
import { toast } from 'react-toastify';
import { useDataTableStateStates } from '@/shared/components/tables/useDataTableState';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useContext } from 'react';
import { ListAllIntershipsOfTheMonthFilterDto } from '@/api.types.extra';

export function useAttendanceSheets(tableState: useDataTableStateStates) {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const [searchParams, setSearchParams] = useSearchParams();
  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const permissaoCriarFolhas = ValidarPermissao(permissoes, [{ role: RoleEnum.FOLHA, level: PermissionEnum.CREATE }]);

  const [loaded, setLoaded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const [registros, setRegistros] = useState<[ListAllIntershipsOfTheMonthResponseDto[], number]>();
  const [filtrosStatus, setFiltrosStatus] = useState<IOpcao[]>([]);
  const [responsaveis, setResponsaveis] = useState<IOpcao[]>([]);
  const [estagiarios, setEstagiarios] = useState<IOpcao[]>([]);

  const [comFolha, setComFolha] = useState(true);
  const [filtroResponsavel, setFiltroResponsavel] = useState<string>();
  const [filtroEstagiario, setFiltroEstagiario] = useState<number>();
  const [statusBusca, setStatusBusca] = useState<AttendanceSheetStatusEnum | number>(0);
  const [tipoVinculoBusca] = useState<InternBondEnum>(InternBondEnum.DEFENSORIA);
  const [unidadeBusca] = useState<number>(0);

  const [selectedItem, setSelectedItem] = useState<ListAllIntershipsOfTheMonthResponseDto>();
  const [folhaSelecionada, setFolhaSelecionada] = useState<ListAllIntershipsOfTheMonthResponseDto>();

  const [showModalCriarFolha, setShowModalCriarFolha] = useState(false);
  const [showModalCriarFolhas, setShowModalCriarFolhas] = useState(false);
  const [showModalNotificacaoIndividual, setShowModalNotificacaoIndividual] = useState(false);
  const [showModalNotificacoes, setShowModalNotificacoes] = useState(false);
  const [showModalResponsavel, setShowModalResponsavel] = useState(false);
  const [showModalAssinaturas, setShowModalAssinaturas] = useState(false);
  const [folhaParaAssinaturas, setFolhaParaAssinaturas] = useState<AttendanceSheetResponseDto | null>(null);

  const [mesBusca, setMesBusca] = useState<Date>(() => {
    const mesParam = searchParams.get('mes');
    if (mesParam) {
      const dt = new Date(mesParam);
      return new Date(dt.getFullYear(), dt.getMonth() + 1, 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  useEffect(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('mes', mesBusca.toISOString().split('T')[0]);

      statusBusca !== 0 ? newParams.set('status', statusBusca.toString()) : newParams.delete('status');
      filtroEstagiario ? newParams.set('estagiario', filtroEstagiario.toString()) : newParams.delete('estagiario');
      filtroResponsavel ? newParams.set('responsavel', filtroResponsavel) : newParams.delete('responsavel');
      newParams.set('comFolha', comFolha.toString());

      return newParams;
    });
  }, [mesBusca, statusBusca, filtroEstagiario, filtroResponsavel, comFolha, setSearchParams]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    if (params.status)
      setStatusBusca(parseInt(params.status));

    if (params.estagiario)
      setFiltroEstagiario(parseInt(params.estagiario));

    if (params.responsavel)
      setFiltroResponsavel(params.responsavel);

    if (params.comFolha)
      setComFolha(params.comFolha === 'true');
  }, [searchParams, estagiarios.length, responsaveis.length]);

  useEffect(() => {
    Promise.all([
      GetPredios(0, 1000),
      readEstagiarioForAttendanceSheetSelect(),
      context.state.username ? readPersonResponsibleSelect() : null,
    ])
      .then(response => {
        // setPredios(response[0]);
        const estagiarios = response[1][0].map(e => ({ label: e.pessoa.nome, value: e.id } as IOpcao));
        estagiarios.unshift({ label: '-', value: 0 });
        setEstagiarios(estagiarios);

        if (response[2]) {
          const responsaveis = response[2][0].map(e => ({
            label: e.nome,
            value: e.id
          } as unknown as IOpcao));
          responsaveis.unshift({ label: '-', value: 0 });
          setResponsaveis(responsaveis);
        }
      })
      .catch(res => toast.error(String(res)))
      .finally(() => setLoaded(true));
  }, []);

  const handleBuscar = () => {
    const ano = mesBusca.getFullYear();
    const mes = mesBusca.getMonth() + 1;

    const dto: ListAllIntershipsOfTheMonthFilterDto = {
      unidade: unidadeBusca,
      status: statusBusca >= 0 ? statusBusca : 0,
      vinculo: tipoVinculoBusca,
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      estagiarioId: filtroEstagiario,
      responsavelId: filtroResponsavel,
    };

    const dtoForSummary = { ...dto, skip: 0, take: 99999, status: 0 };

    Promise.all([
      listAllIntershipsOfTheMonth(ano, mes, dto),
      listAllIntershipsOfTheMonth(ano, mes, dtoForSummary),
    ])
      .then(result => {
        const resultadoConsulta = result[0];
        const sumario = result[1];

        if (statusBusca == -1)
          resultadoConsulta[0] = resultadoConsulta[0].filter(a => a.folha?.status !== AttendanceSheetStatusEnum.CONCLUIDA);

        resultadoConsulta[0] = resultadoConsulta[0].filter(a => (a.folha !== null) === comFolha);
        setRegistros(resultadoConsulta);

        const tStatus = getAttendanceSheetStatusEnumOptions().map(item => {
          const qtd = sumario[0].filter(a => a.folha?.status === item.value).length;
          return { value: Number(item.value), label: `${item.label} ${qtd > 0 ? `(${qtd})` : ''}` };
        });

        const qtdExcetoConcluidos = sumario[0].filter(a => a.folha?.status !== AttendanceSheetStatusEnum.CONCLUIDA).length;
        tStatus.unshift({ value: -1, label: `Exceto concluídos ${qtdExcetoConcluidos > 0 ? `(${qtdExcetoConcluidos})` : ''}` });
        setFiltrosStatus(tStatus);
      })
      .catch(res => toast.error(String(res)))
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (!loaded) return;
    handleBuscar();
  }, [unidadeBusca, mesBusca, statusBusca, tipoVinculoBusca, loaded, showModalCriarFolhas, comFolha, filtroEstagiario, filtroResponsavel]);

  const handleGenerateAttendanceSheetsForMonth = async () => {
    setIsExecuting(true);
    generateAttendanceSheetsForMonth(mesBusca.getFullYear(), mesBusca.getMonth() + 1)
      .then(result => toast.success(`${result.length} folhas de frequência geradas.`))
      .catch(res => toast.error(String(res)))
      .finally(() => setIsExecuting(false));
  };

  const handleCriarFolha = () => {
    if (!selectedItem) return;
    setIsExecuting(true);
    createAttendanceSheet(selectedItem.estagio.id, selectedItem.ano, selectedItem.mes)
      .then(res => toast.success(res ? 'Folha Criada' : String(res)))
      .catch(res => toast.error(String(res)))
      .finally(() => setIsExecuting(false));
  };

  return {
    mesBusca, setMesBusca,
    statusBusca, setStatusBusca,
    filtroResponsavel, setFiltroResponsavel,
    filtroEstagiario, setFiltroEstagiario,
    filtrosStatus, registros, handleBuscar,
    comFolha, setComFolha,
    responsaveis, estagiarios,
    permissaoCriarFolhas,
    isExecuting, handleCriarFolha, handleGenerateAttendanceSheetsForMonth,
    selectedItem, setSelectedItem,
    folhaSelecionada, setFolhaSelecionada,
    showModalCriarFolha, setShowModalCriarFolha,
    showModalCriarFolhas, setShowModalCriarFolhas,
    showModalNotificacaoIndividual, setShowModalNotificacaoIndividual,
    showModalNotificacoes, setShowModalNotificacoes,
    showModalResponsavel, setShowModalResponsavel,
    showModalAssinaturas, setShowModalAssinaturas,
    folhaParaAssinaturas, setFolhaParaAssinaturas,
    loaded,
  };
}
